import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import {
  useHistory,
  useLocation,
  useParams,
  useRouteMatch,
} from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { toast } from "bulma-toast";
import { HiOutlineStatusOnline } from "react-icons/hi";
import { BiTransfer } from "react-icons/bi";
import { useStore } from "../../../stores";
import routes from "../../../constants/routes";
import locale from "../../../constants/locale";
import {
  AddonEnum,
  ChatChannel,
  chatPriorityColorMap,
  chatPriorityMap,
  MetaType,
} from "../../../models";
import { Message as MessageModel } from "../../../models";
import { getAttachment } from "../../../utils/messages";
import Message from "./Message";
import MessageComposer from "./messageComposer/MessageComposer";
import ChatInfo from "./ChatInfo";
import CallContainer from "./CallContainer";
import styles from "./Conversation.module.scss";
import StarIcon from "./StarIcon";
import useMobileResponsive from "../../../hooks/useMobileResponsive";
import { FallbackImage } from "../../../components/fallbackImage/FallbackImage";
import { Popover } from "react-tiny-popover";
import BotmateAIPopover from "./components/BotmateAI/BotmateAIPopover";
import EventIframe from "./messageComposer/EventIframe";
import { Avatar, Tooltip } from "antd";
import { Loader } from "../../../components";
import PinedMessage from "./PinedMessage";
import GuestUserAdmin from "./components/GuestUserAdmin";
import DialVoiceCall from "./components/DialVoiceCall";
import ConversationSearch from "./ConversationSearch";
import { GroupedActivityLogs } from "./GroupedActivityLogs";
import ChatWorkflows from "./ChatWorkflows";

const Conversation = ({ embeddedChatId }: { embeddedChatId?: number } = {}) => {
  const { t } = useTranslation();
  const {
    selectedChat,
    messages: allMessages,
    loadMessages,
    markAsRead,
    sendVideoRequest,
    toggleStarredChat,
    setSelectedChat,
  } = useStore("chats");
  const { selectedBot } = useStore("bots");
  const { user } = useStore("auth");
  const { chatId: paramChatId } = useParams<{
    chatId?: string;
  }>();
  const chatId =
    embeddedChatId !== undefined ? String(embeddedChatId) : paramChatId;
  const embedded = embeddedChatId !== undefined;
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const notesOpen = queryParams.get("notes");
  const selectedMessageId = queryParams.get("selectedMessageId");
  const divRef = useRef<HTMLDivElement | null>(null);
  const match = useRouteMatch();
  const [showInfo, setShowInfo] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [lastMsg, setLastMsg] = useState<string | number>(0);
  const [videoRequestLoading, setVideoRequestLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const isMobile = useMobileResponsive();

  const [dragging, setDragging] = useState(false);
  const [showChatWorkflows, setShowChatWorkflows] = useState(false);

  const handleDragEnter = (e: any) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = (e: any) => {
    e.preventDefault();
    setDragging(false);
  };

  const handleDragOver = (e: any) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDrop = (e: any) => {
    e.preventDefault();
    setDragging(false);

    const files = [...e.dataTransfer.files];
    const customEvent = new CustomEvent("handleFilesDropped", {
      detail: { files },
    });
    document.dispatchEvent(customEvent);
  };

  const groupActivityLogMessages = useCallback(
    (messages: MessageModel[]) => {
      const result: (
        | MessageModel
        | {
            type: "grouped-activity-logs";
            messages: MessageModel[];
            id: string;
          }
      )[] = [];
      let currentGroup: MessageModel[] = [];

      // If chat history is disabled, filter out activity logs
      const shouldDisableActivityLogs =
        selectedBot?.settings?.disableChatHistory;

      messages.forEach((message, index) => {
        const isActivityLog =
          message.metaType === MetaType.ActivityLog && !message.text;

        // Skip activity logs if chat history is disabled
        if (isActivityLog && shouldDisableActivityLogs) {
          return;
        }

        if (isActivityLog) {
          currentGroup.push(message);
        } else {
          // If we have a group of 2+ activity logs, create a grouped entry
          if (currentGroup.length >= 2) {
            result.push({
              type: "grouped-activity-logs",
              messages: currentGroup,
              id: `grouped-${currentGroup[0].id}-${
                currentGroup[currentGroup.length - 1].id
              }`,
            });
          } else if (currentGroup.length === 1) {
            // Single activity log, add normally
            result.push(currentGroup[0]);
          }

          currentGroup = [];
          result.push(message);
        }
      });

      // Handle any remaining group at the end
      if (currentGroup.length >= 2) {
        result.push({
          type: "grouped-activity-logs",
          messages: currentGroup,
          id: `grouped-${currentGroup[0].id}-${
            currentGroup[currentGroup.length - 1].id
          }`,
        });
      } else if (currentGroup.length === 1) {
        result.push(currentGroup[0]);
      }

      return result;
    },
    [selectedBot?.settings?.disableChatHistory]
  );

  const messages = useMemo(() => {
    let filteredMessages = allMessages;

    if (selectedChat?.channel === ChatChannel.FBComment) {
      filteredMessages = allMessages.filter((m) => !m.mId?.includes("|"));
    }

    return groupActivityLogMessages(filteredMessages);
  }, [allMessages, selectedChat?.channel, groupActivityLogMessages]);

  const totalMessageCount = useMemo(() => {
    return messages.reduce((count, item) => {
      if (
        typeof item === "object" &&
        "type" in item &&
        item.type === "grouped-activity-logs"
      ) {
        return count + item.messages.length;
      }
      return count + 1;
    }, 0);
  }, [messages]);

  const highlightMessage = useCallback(() => {
    if (!selectedMessageId) {
      return;
    }
    queryParams.delete("selectedMessageId");
    const newSearch = queryParams.toString();
    history.replace({
      ...location,
      search: newSearch ? `?${newSearch}` : "",
    });
    setTimeout(() => {
      const messageElement = document.querySelector(
        `#chat-message-${selectedMessageId}`
      );
      if (messageElement) {
        messageElement.scrollIntoView({ behavior: "smooth" });

        // Highlight the message temporarily
        messageElement.classList.add(styles.highlightedMessage);
        setTimeout(() => {
          messageElement.classList.remove(styles.highlightedMessage);
        }, 2000);
      }
    }, 500);
    // eslint-disable-next-line
  }, [selectedMessageId, history, location]);

  useEffect(() => {
    if (!selectedChat?.id || selectedChat.id !== Number(chatId)) {
      return;
    }

    const load = async () => {
      setLoading(true);
      setHasMoreMessages(true);
      await loadMessages(200, false);
      setLoading(false);
      highlightMessage();
    };

    load();
  }, [
    loadMessages,
    selectedChat?.id,
    chatId,
    setSelectedChat,
    highlightMessage,
  ]);

  useEffect(() => {
    if (!selectedChat?.id || selectedChat.id !== Number(chatId)) {
      return;
    }
    if (embeddedChatId !== undefined) {
      return;
    }

    return () => {
      setSelectedChat(undefined);
    };
  }, [selectedChat?.id, chatId, setSelectedChat, embeddedChatId]);

  useEffect(() => {
    if (!divRef.current || !messages?.length) {
      return;
    }

    const lastItem = messages[messages.length - 1];
    const lastItemId = lastItem.id;

    if (lastMsg === lastItemId) {
      return;
    }

    setLastMsg(lastItemId);

    divRef.current.scrollTop = divRef.current.scrollHeight + 100;

    if (selectedChat?.isConnected && selectedChat.userConnected === user?.id) {
      markAsRead();
    }
  }, [
    lastMsg,
    markAsRead,
    messages,
    selectedChat?.isConnected,
    selectedChat?.userConnected,
    user?.id,
  ]);

  useEffect(() => {
    if (notesOpen) {
      setShowInfo(true);
    }
  }, [notesOpen]);

  const handleVideoRequest = useCallback(async () => {
    setVideoRequestLoading(true);

    const sent = await sendVideoRequest();
    if (!sent) {
      toast({
        message: t(locale.genericError),
        position: "top-right",
        dismissible: true,
        type: "is-danger",
        pauseOnHover: true,
      });
    }

    setVideoRequestLoading(false);
  }, [sendVideoRequest, t]);

  const videoCallMessage = useMemo(
    () =>
      messages
        .slice()
        .reverse()
        .find((item) => {
          if ("type" in item && item.type === "grouped-activity-logs") {
            return false;
          }
          const f = item as MessageModel;
          const attachment = getAttachment(f);

          return (
            attachment?.type === "video_call" &&
            attachment?.payload?.token &&
            attachment?.payload?.status === "accepted"
          );
        }) as MessageModel | undefined,
    [messages]
  );

  if (!selectedChat || selectedChat.id !== Number(chatId)) {
    return (
      <div className="chat-bg full-screen-mobile pb-3 is-flex is-flex-grow-1 is-flex-direction-column has-border-right"></div>
    );
  }

  const ongoingCall =
    !!videoCallMessage &&
    !!selectedChat.disableVideoCall &&
    selectedChat.userConnected === user?.id;

  return (
    <>
      <section
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        id="section-conversation"
        className={clsx(
          "full-screen-mobile is-flex is-flex-grow-1 is-flex-direction-column has-border-right",
          styles.container,
          {
            "pb-3 chat-bg": !isMobile,
            "is-active": match.path === routes.chats && chatId && !showInfo,
            [styles.dragging]: dragging,
          }
        )}
      >
        <ConversationHeader
          user={user}
          selectedBot={selectedBot}
          selectedChat={selectedChat}
          videoRequestLoading={videoRequestLoading}
          handleVideoRequest={handleVideoRequest}
          toggleStarredChat={toggleStarredChat}
          showInfo={showInfo}
          setShowInfo={setShowInfo}
          isMobile={isMobile}
          setShowChatWorkflows={setShowChatWorkflows}
        />

        <div className="is-flex is-flex-direction-column is-align-items-stretch is-justify-content-flex-end is-flex-grow-1 is-relative">
          {ongoingCall && !!videoCallMessage && (
            <CallContainer message={videoCallMessage} />
          )}
          {selectedChat?.data?.post && (
            <a
              href={
                JSON.parse(selectedChat?.data?.post)?.attachments?.data?.[0]
                  ?.url
              }
              target="_blank"
              rel="noreferrer"
            >
              {t(locale.viewSource, {
                type: `"${JSON.parse(selectedChat?.data?.post)?.message}"`,
              })}
            </a>
          )}
          <div
            ref={divRef}
            id="messages_scroll"
            className={clsx(styles.scrollContainer, {
              [styles.callScrollContainer]: ongoingCall,
            })}
          >
            <InfiniteScroll
              style={{ overflowX: "hidden" }}
              dataLength={totalMessageCount || 0}
              next={async () => {
                if (!loading) {
                  setLoading(true);
                  const newMessages = await loadMessages(100, true);
                  setHasMoreMessages((newMessages ?? []).length >= 100);
                  setLoading(false);
                }
              }}
              hasMore={totalMessageCount >= 200 && hasMoreMessages && !loading}
              loader={null}
              scrollableTarget="messages_scroll"
              inverse
            >
              {messages.map((item) => {
                if (
                  typeof item === "object" &&
                  "type" in item &&
                  item.type === "grouped-activity-logs"
                ) {
                  return (
                    <GroupedActivityLogs
                      key={item.id}
                      messages={item.messages}
                    />
                  );
                }
                return (
                  <Message
                    key={(item as MessageModel).id}
                    message={item as MessageModel}
                  />
                );
              })}
            </InfiniteScroll>
            {ongoingCall && <div className={styles.gradient} />}
          </div>
        </div>
        <MessageComposer ongoingCall={ongoingCall} embedded={embedded} />

        {loading && (
          <div className="loaderClass">
            <Loader />
          </div>
        )}
      </section>
      {(embedded || match.path === routes.chats) && chatId && showInfo && (
        <ChatInfo
          onClose={() => setShowInfo(false)}
          defaultTab={notesOpen ? "notes" : undefined}
        />
      )}
      {showChatWorkflows && (
        <ChatWorkflows
          chatId={selectedChat.id.toString()}
          onClose={() => setShowChatWorkflows(false)}
        />
      )}
    </>
  );
};

function ConversationHeader({
  user,
  selectedBot,
  selectedChat,
  isMobile,
  videoRequestLoading,
  handleVideoRequest,
  toggleStarredChat,
  showInfo,
  setShowInfo,
  setShowChatWorkflows,
}: any) {
  const { users } = useStore("users");
  const { isAddonActive, routeTo } = useStore("bots");
  const { updateConversationStatus } = useStore("chats");
  const { userHasAdminAccess } = useStore("auth");
  const [isStarredPopoverOpen, setIsStarredPopoverOpen] = useState(false);
  const [isStatusPopoverOpen, setIsStatusPopoverOpen] = useState(false);
  const userIsAdmin = userHasAdminAccess;

  const history = useHistory();
  const { t } = useTranslation();

  const onBack = useCallback(
    () => history.push(routeTo(routes.chats.replace(":chatId?", ""))),
    [history, routeTo]
  );

  const MentionedUsers = ({ userIds }: any) => {
    if (!userIds) return null;

    const userIdsMap = JSON.parse(userIds);

    return (
      <Avatar.Group
        maxCount={2}
        maxPopoverTrigger="hover"
        maxStyle={{
          cursor: "pointer",
        }}
      >
        {userIdsMap.map((userId: any) => {
          const user = users?.find((u) => u.id === userId);
          if (!user) return null;
          return (
            <Tooltip className="is-clickable" title={user.name}>
              <Avatar src={user.avatarUrl || "/assets/avatar.svg"} />
            </Tooltip>
          );
        })}
      </Avatar.Group>
    );
  };

  if (isMobile) {
    return (
      <div className="has-background-light has-border py-1">
        <div className="is-flex is-justify-content-space-between">
          <div className="is-flex">
            <button className="button is-ghost no-focus" onClick={onBack}>
              <span className="icon">
                <img src="/assets/chevron-left.svg" alt="" />
              </span>
            </button>
            <figure className="is-flex-shrink-0">
              <p className="image is-32x32 is-square">
                <FallbackImage
                  className="is-rounded"
                  src={selectedChat.image}
                  fallback="/assets/avatar.svg"
                  alt={selectedChat.firstName}
                />
              </p>
            </figure>
          </div>

          <div className="is-flex">
            <MentionedUsers
              userIds={selectedChat.data?.e_cus_mentioned_users}
            />
            {selectedChat.channel === ChatChannel.WebChat &&
              !!selectedBot?.hasVideoCallEnabled &&
              isAddonActive(AddonEnum.VIDEO_CALL) &&
              false &&
              selectedChat.isConnected && (
                <button
                  className={clsx("button is-ghost no-focus", {
                    "is-loading": videoRequestLoading,
                  })}
                  disabled={selectedChat.disableVideoCall}
                  onClick={handleVideoRequest}
                >
                  <span className="icon">
                    <img src="/assets/video.svg" alt="" />
                  </span>
                </button>
              )}
            {userIsAdmin && !selectedChat.isConnected && (
              <EventIframe position="header" isResponsive />
            )}
            {userIsAdmin && !selectedChat.isConnected && (
              <BotmateAIPopover
                setIsLoading={() => {}}
                message={""}
                setMessage={() => {}}
                position="header"
                isResponsive={true}
              />
            )}
            <GuestUserAdmin isResponsive />
            {!!selectedChat.forwardBot &&
              Number(selectedChat.data?.forward_source_bot_id) !== selectedBot?.id && (
                <Tooltip title={`Transferred from Bot #${selectedChat.forwardBot}`}>
                  <button className="button is-ghost no-focus">
                    <span className="icon">
                      <BiTransfer size={18} color="#5BC0DE" />
                    </span>
                  </button>
                </Tooltip>
              )}
            <Popover
              isOpen={isStarredPopoverOpen}
              positions={["bottom"]}
              containerStyle={{ zIndex: "100000000" }}
              onClickOutside={() => setIsStarredPopoverOpen(false)}
              content={() => (
                <div className="has-border has-background-white has-rounded-corners">
                  {Object.keys(chatPriorityColorMap).map((m) => (
                    <div
                      onClick={() => {
                        toggleStarredChat(true, parseInt(m));
                        setIsStarredPopoverOpen(false);
                      }}
                      style={{ gap: "3px" }}
                      className="has-border-top is-align-items-center is-clickable is-flex px-4 py-2"
                    >
                      <StarIcon
                        color={chatPriorityColorMap[m as never]}
                        strokeColor={
                          parseInt(m) === selectedChat?.priority
                            ? chatPriorityColorMap[
                                selectedChat?.priority as never
                              ]
                            : undefined
                        }
                      />
                      <span className="is-7 mb-0 title">
                        {t(chatPriorityMap[m as never])}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            >
              <button
                className={clsx("button is-ghost no-focus")}
                onClick={() => setIsStarredPopoverOpen(true)}
              >
                <span className="icon">
                  <StarIcon
                    color={
                      chatPriorityColorMap[selectedChat?.priority as never]
                    }
                  />
                </span>
              </button>
            </Popover>
            {selectedChat?.isConnected && <ConversationSearch isMobile />}
            {userIsAdmin && (
              <button
                className="button is-ghost no-focus"
                onClick={() => setShowChatWorkflows(true)}
                title="View Chat Workflows"
              >
                <span className="icon">
                  <img src="/assets/workflow.svg" alt="Workflows" />
                </span>
              </button>
            )}
            <button
              className="button is-ghost no-focus"
              onClick={() => setShowInfo(!showInfo)}
            >
              <span className="icon">
                <img src="/assets/three-dots.svg" alt="" />
              </span>
            </button>
          </div>
        </div>
        <PinedMessage />
      </div>
    );
  }

  return (
    <div className={clsx(styles.header)}>
      <DialVoiceCall />
      <PinedMessage />
      <MentionedUsers userIds={selectedChat.data?.e_cus_mentioned_users} />
      {selectedChat.channel === ChatChannel.WebChat &&
        !!selectedBot?.hasVideoCallEnabled &&
        isAddonActive(AddonEnum.VIDEO_CALL) &&
        false &&
        selectedChat.isConnected && (
          <button
            className={clsx("button is-rounded has-text-primary", {
              "is-loading": videoRequestLoading,
            })}
            disabled={selectedChat.disableVideoCall}
            onClick={handleVideoRequest}
          >
            <span style={{ position: "absolute" }} className="icon">
              <img src="/assets/video.svg" alt="" />
            </span>
          </button>
        )}
      {userIsAdmin && !selectedChat.isConnected && (
        <EventIframe position="header" />
      )}
      {userIsAdmin && !selectedChat.isConnected && (
        <BotmateAIPopover
          setIsLoading={() => {}}
          message={""}
          setMessage={() => {}}
          position="header"
        />
      )}
      <GuestUserAdmin />
      {!!selectedChat.forwardBot &&
        Number(selectedChat.data?.forward_source_bot_id) !== selectedBot?.id && (
          <Tooltip title={`Transferred from Bot #${selectedChat.forwardBot}`}>
            <button className="button is-rounded" style={{ pointerEvents: "auto" }}>
              <span style={{ position: "absolute" }} className="icon">
                <BiTransfer size={18} color="#5BC0DE" />
              </span>
            </button>
          </Tooltip>
        )}
      {selectedBot?.settings?.conversationStatusConfig?.enabled && (
        <Popover
          isOpen={isStatusPopoverOpen}
          positions={["bottom"]}
          containerStyle={{ zIndex: "100000000" }}
          onClickOutside={() => setIsStatusPopoverOpen(false)}
          content={() => (
            <div className="has-border has-background-white has-rounded-corners">
              {selectedBot?.settings?.conversationStatusConfig?.statuses?.map(
                (status: any) => (
                  <div
                    key={status.id}
                    onClick={async () => {
                      await updateConversationStatus(status.id);
                      setIsStatusPopoverOpen(false);
                    }}
                    style={{
                      gap: "3px",
                    }}
                    className="has-border-top is-align-items-center is-clickable is-flex px-4 py-2"
                  >
                    <div
                      style={{
                        width: "12px",
                        height: "12px",
                        borderRadius: "50%",
                        border: "2px solid " + (status.color || "#55CD6C"),
                        marginRight: "8px",
                      }}
                    />
                    <span
                      className="is-7 mb-0 title"
                      style={{
                        color:
                          selectedChat?.conversationStatusId === status.id
                            ? "#55CD6C"
                            : "",
                      }}
                    >
                      {status.name}
                    </span>
                  </div>
                )
              )}
            </div>
          )}
        >
          <button
            className={clsx("button is-rounded", {
              "has-text-primary": selectedChat.conversationStatusId,
            })}
            onClick={() => setIsStatusPopoverOpen(true)}
          >
            <span style={{ position: "absolute" }} className="icon">
              <HiOutlineStatusOnline
                size={20}
                color={
                  selectedChat.conversationStatusId
                    ? selectedBot?.settings?.conversationStatusConfig?.statuses?.find(
                        (s: any) => s.id === selectedChat.conversationStatusId
                      )?.color
                    : "#dee1e6"
                }
              />
            </span>
          </button>
        </Popover>
      )}
      <Popover
        isOpen={isStarredPopoverOpen}
        positions={["bottom"]}
        containerStyle={{ zIndex: "100000000" }}
        onClickOutside={() => setIsStarredPopoverOpen(false)}
        content={() => (
          <div className="has-border has-background-white has-rounded-corners">
            {Object.keys(chatPriorityColorMap).map((m) => (
              <div
                onClick={() => {
                  toggleStarredChat(true, parseInt(m));
                  setIsStarredPopoverOpen(false);
                }}
                style={{ gap: "3px" }}
                className="has-border-top is-align-items-center is-clickable is-flex px-4 py-2"
              >
                <StarIcon
                  color={chatPriorityColorMap[m as never]}
                  strokeColor={
                    parseInt(m) === selectedChat?.priority
                      ? chatPriorityColorMap[selectedChat?.priority as never]
                      : undefined
                  }
                />
                <span className="is-7 mb-0 title">
                  {t(chatPriorityMap[m as never])}
                </span>
              </div>
            ))}
          </div>
        )}
      >
        <button
          className={clsx("button is-rounded has-text-primary", {
            [styles.starIcon]: selectedChat.starred,
          })}
          onClick={() => setIsStarredPopoverOpen(true)}
        >
          <span style={{ position: "absolute" }} className="icon">
            <StarIcon
              color={chatPriorityColorMap[selectedChat?.priority as never]}
            />
          </span>
        </button>
      </Popover>
      {selectedChat.isConnected && <ConversationSearch />}
      {userIsAdmin && (
        <button
          className={clsx("button is-rounded")}
          onClick={() => setShowChatWorkflows(true)}
          title="View Chat Workflows"
        >
          <span style={{ position: "absolute" }} className="icon">
            <img src="/assets/workflow.svg" alt="Workflows" />
          </span>
        </button>
      )}
      <button
        className={clsx(styles.noBtn, {
          "has-text-primary": showInfo,
        })}
        onClick={() => setShowInfo(!showInfo)}
      >
        <span className="icon">
          <img src="/assets/three-dots.svg" alt="" />
        </span>
      </button>
    </div>
  );
}

export default observer(Conversation);
